// ProductionOrders.js - Admin page for M9 ProductionOrder: the workshop's
// physical work of making goods (distinct from a client's Order). List,
// create (starts production - reserves raw material via the product/size's
// BOM), advance status through the full workshop step list, cancel.
// "Livré" mid-sequence is an INTERNAL handoff (piece moves to another
// workshop/factory to continue) - not the client-facing Order.status
// "livré". "Contrôle qualité" is the terminal step: reaching it credits
// finished stock.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../Stock/Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
} from "@mui/material";
import { userRequest } from "../../RequestMethods";
import { getColorDisplayName } from "../../utils/colorNames";
import { STATUS_ORDER, TERMINAL_STATUS, nextStatus } from "../../Data/productionStatuses";
import { ProductionCycleTimelineCompact } from "../../Components/ProductionCycleTimeline/ProductionCycleTimeline";

const emptyNewOrder = {
  productId: "", designId: "", color: "", size: "", quantity: "1",
  assignedTo: "", isRush: false, workshopRef: "", dueDate: "", orderId: "",
};

const ProductionOrders = () => {
  const navigate = useNavigate();
  const [productionOrders, setProductionOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const [products, setProducts] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newOrder, setNewOrder] = useState(emptyNewOrder);
  const [creating, setCreating] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);

  const fetchProductionOrders = async (status = statusFilter) => {
    try {
      setLoading(true);
      const params = status ? { status } : {};
      const res = await userRequest.get("production-orders", { params });
      setProductionOrders(res.data.productionOrders || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des ordres de production.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductionOrders();
    userRequest.get("products/dropdown", { params: { _: Date.now() } })
      .then((res) => setProducts(res.data.products || []))
      .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du chargement des produits."));
    userRequest.get("designs", { params: { limit: 500 } })
      .then((res) => setDesigns((res.data.designs || []).filter((d) => d.isPublishedAdmin !== false)))
      .catch((err) => toast.error(err?.response?.data?.message || "Erreur lors du chargement des modèles."));
    // Assigning a specific staff member is optional - if this worker's role
    // can't read the workers list (workers:read is Super Admin only by
    // default), just skip the assignee picker instead of failing the page.
    userRequest.get("workers")
      .then((res) => setWorkers(res.data.workers || []))
      .catch(() => setWorkers([]));
    // Optional link to a real client Order (bulk/speculative production has
    // none) - getAllOrders 404s when there are none, so just fall back to [].
    userRequest.get("orders")
      .then((res) => setOrders(res.data.orders || []))
      .catch(() => setOrders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (status) => {
    setStatusFilter(status);
    fetchProductionOrders(status);
  };

  // A Design always belongs to exactly one Product (Design.linkedProduct is
  // required) - so this is one choice with two entry points, never two
  // independent pickers: choosing a plain product clears any design, and
  // choosing a design resolves (and locks in) its product automatically.
  const handleSelectProduct = async (productId, designId = "") => {
    setNewOrder({ ...newOrder, productId, designId, color: "", size: "" });
    setSelectedProductDetail(null);
    if (!productId) return;
    try {
      const res = await userRequest.get(`products/${encodeURIComponent(productId)}`);
      setSelectedProductDetail(res.data.product);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement du produit.");
    }
  };

  // Picking a design resolves (and locks in) its product automatically;
  // clearing the design just drops the design link and keeps the product.
  const handleSelectDesign = (design) => {
    if (design) return handleSelectProduct(design.linkedProduct, design._id);
    setNewOrder((prev) => ({ ...prev, designId: "" }));
  };

  const isFinished = selectedProductDetail?.isFinishedProduct === true;
  const availableColors = selectedProductDetail && !isFinished ? Object.keys(selectedProductDetail.colorsAndSizes || {}) : [];
  const availableSizes = isFinished
    ? Object.keys(selectedProductDetail?.sizesOnly || {})
    : Object.keys((selectedProductDetail?.colorsAndSizes || {})[newOrder.color] || {});

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    if (!newOrder.productId || !newOrder.size || !newOrder.quantity) {
      toast.error("Produit, taille et quantité requis.");
      return;
    }
    try {
      setCreating(true);
      await userRequest.post("production-orders", {
        productId: newOrder.productId,
        designId: newOrder.designId || null,
        color: newOrder.color,
        size: newOrder.size,
        quantity: Number(newOrder.quantity),
        assignedTo: newOrder.assignedTo || null,
        isRush: newOrder.isRush,
        workshopRef: newOrder.workshopRef,
        dueDate: newOrder.dueDate || null,
        orderId: newOrder.orderId || null,
      });
      toast.success("Production démarrée.");
      setNewOrder(emptyNewOrder);
      setSelectedProductDetail(null);
      fetchProductionOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Erreur lors du démarrage de la production.");
    } finally {
      setCreating(false);
    }
  };

  const handleAdvance = async (productionOrder) => {
    const to = nextStatus(productionOrder.status);
    if (!to) return;
    try {
      await userRequest.post(`production-orders/${productionOrder._id}/advance`, { toStatus: to });
      toast.success(to === TERMINAL_STATUS ? "Production terminée - stock crédité." : "Étape suivante.");
      fetchProductionOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors du changement de statut.");
    }
  };

  const handleCancel = async (productionOrder) => {
    if (!window.confirm("Annuler cet ordre de production ? Le matériel réservé sera libéré.")) return;
    try {
      await userRequest.delete(`production-orders/${productionOrder._id}`);
      toast.success("Ordre de production annulé.");
      fetchProductionOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors de l'annulation.");
    }
  };

  return (
    <div className="Stock">
      <h1>Production</h1>

      <section className="stock-section">
        <h2>Démarrer une production</h2>
        <form className="stock-new-line-form" onSubmit={handleCreateOrder} style={{ flexWrap: "wrap" }}>
          <Autocomplete
            options={products}
            getOptionLabel={(p) => p.title || p._id}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return <li key={option._id} {...rest}>{option.title || option._id}</li>;
            }}
            value={products.find((p) => p._id === newOrder.productId) || null}
            onChange={(e, val) => handleSelectProduct(val ? val._id : "")}
            renderInput={(params) => (
              <TextField {...params} label="Produit" placeholder="Rechercher un produit..." size="small" />
            )}
            sx={{ minWidth: 240 }}
          />
          <Autocomplete
            options={designs}
            getOptionLabel={(d) => d.title || d._id}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return <li key={option._id} {...rest}>{option.title || option._id}</li>;
            }}
            value={designs.find((d) => d._id === newOrder.designId) || null}
            onChange={(e, val) => handleSelectDesign(val)}
            renderInput={(params) => (
              <TextField {...params} label="Modèle (optionnel)" placeholder="Rechercher un modèle..." size="small" />
            )}
            sx={{ minWidth: 240 }}
          />
          {!isFinished && (
            <select value={newOrder.color} onChange={(e) => setNewOrder({ ...newOrder, color: e.target.value, size: "" })}>
              <option value="">Couleur...</option>
              {availableColors.map((c) => <option key={c} value={c}>{getColorDisplayName(c)}</option>)}
            </select>
          )}
          <select value={newOrder.size} onChange={(e) => setNewOrder({ ...newOrder, size: e.target.value })}>
            <option value="">Taille...</option>
            {availableSizes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            type="number" placeholder="Quantité" min="1" value={newOrder.quantity}
            onChange={(e) => setNewOrder({ ...newOrder, quantity: e.target.value })}
          />
          {workers.length > 0 && (
            <select value={newOrder.assignedTo} onChange={(e) => setNewOrder({ ...newOrder, assignedTo: e.target.value })}>
              <option value="">Assigner à...</option>
              {workers.map((w) => <option key={w._id} value={w._id}>{w.name} {w.surname}</option>)}
            </select>
          )}
          <Autocomplete
            options={orders}
            getOptionLabel={(o) => `${o.company} — n° ${o.number}`}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return <li key={option._id} {...rest}>{option.company} — n° {option.number}</li>;
            }}
            value={orders.find((o) => o._id === newOrder.orderId) || null}
            onChange={(e, val) => setNewOrder({ ...newOrder, orderId: val ? val._id : "" })}
            renderInput={(params) => (
              <TextField {...params} label="Commande liée (optionnel)" placeholder="Rechercher une commande..." size="small" />
            )}
            sx={{ minWidth: 260 }}
          />
          <input
            type="text" placeholder="Réf. atelier / GTR" value={newOrder.workshopRef}
            onChange={(e) => setNewOrder({ ...newOrder, workshopRef: e.target.value })}
          />
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <input type="checkbox" checked={newOrder.isRush} onChange={(e) => setNewOrder({ ...newOrder, isRush: e.target.checked })} />
            Urgent
          </label>
          <button type="submit" className="action-icon-btn btn-primary" disabled={creating}>
            {creating ? "Démarrage..." : "Démarrer la production"}
          </button>
        </form>
        <div className="field-note">
          Réserve automatiquement la matière première nécessaire (selon la consommation renseignée sur la fiche produit).
        </div>
      </section>

      <section className="stock-section">
        <h2>Ordres de production</h2>
        <div className="stock-filters">
          <select value={statusFilter} onChange={(e) => handleFilterChange(e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="loading-container"><div className="loading-spinner-custom"></div></div>
        ) : (
          <TableContainer component={Paper} className="stock-table-container">
            <Table>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell sx={{ fontWeight: "bold" }}>Produit</TableCell>
                  <TableCell>Couleur / Taille</TableCell>
                  <TableCell>Qté</TableCell>
                  <TableCell>Avancement</TableCell>
                  <TableCell>Assigné à</TableCell>
                  <TableCell>Urgent</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productionOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Aucun ordre de production.</TableCell>
                  </TableRow>
                )}
                {productionOrders.map((po) => (
                  <TableRow key={po._id}>
                    <TableCell>
                      <span
                        className="production-order-link"
                        onClick={() => navigate(`/production-orders/${po._id}`)}
                      >
                        {po.productTitle}{po.designId?.title ? ` — ${po.designId.title}` : ""}
                      </span>
                    </TableCell>
                    <TableCell>{po.color ? getColorDisplayName(po.color) : "—"} / {po.size}</TableCell>
                    <TableCell>{po.quantity}</TableCell>
                    <TableCell><ProductionCycleTimelineCompact status={po.status} /></TableCell>
                    <TableCell>{po.assignedTo ? `${po.assignedTo.name || ""} ${po.assignedTo.surname || ""}`.trim() : "—"}</TableCell>
                    <TableCell>{po.isRush ? "⚡" : "—"}</TableCell>
                    <TableCell>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {po.status !== TERMINAL_STATUS && (
                          <button className="action-icon-btn btn-primary" onClick={() => handleAdvance(po)}>
                            → {nextStatus(po.status)}
                          </button>
                        )}
                        {po.status !== TERMINAL_STATUS && (
                          <button className="action-icon-btn btn-delete" onClick={() => handleCancel(po)}>Annuler</button>
                        )}
                        <button className="action-icon-btn btn-edit" onClick={() => navigate(`/production-orders/${po._id}`)}>
                          Détails
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default ProductionOrders;
